import { useMediaQuery } from '@sohanemon/utils/hooks';
import type { Transition } from 'motion/react';
import * as React from 'react';
import { motionVariants } from './variants';

type MotionQuery<T extends React.ElementType> = {
	breakpoint?: Parameters<typeof useMediaQuery>[0];
	mediaProps?: React.ComponentProps<T>;
	delay?: number;
	duration?: number;
	always?: boolean;
};

export function withVariants<T extends React.ElementType>(Comp: T) {
	type Props = React.ComponentProps<T> & MotionQuery<T>;

	function Wrapped({
		transition,
		always,
		whileInView,
		breakpoint = 'lg',
		mediaProps,
		delay,
		duration = 0.5,
		...props
	}: Props) {
		const matches = useMediaQuery(breakpoint);
		const [mounted, setMounted] = React.useState(false);
		React.useEffect(() => setMounted(true), []);
		const isSmallScreen = mounted && !matches;
		const {
			transition: mediaTransition,
			whileInView: mediaInView,
			...mediaRest
		} = (isSmallScreen ? mediaProps : undefined) ?? {};

		const componentProps = {
			variants: motionVariants,
			viewport: { once: !always },
			whileInView:
				mediaInView ?? whileInView ?? (props.animate ? undefined : 'visible'),
			...props,
			...mediaRest,
			transition: {
				type: 'spring',
				duration,
				delay,
				...transition,
				...mediaTransition,
			} as Transition,
		};

		return React.createElement(Comp, componentProps as React.ComponentProps<T>);
	}

	return Wrapped;
}
